<template>
  <div class="card mb-4">

    <!-- UPDATED: Responsive flex classes to prevent overlap in header -->
    <div class="card-header pb-0 d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3">
      <h6 class="mb-0">Device Group</h6>
      <div class="d-flex align-items-center w-100 w-md-auto justify-content-between justify-content-md-end">
        <!-- VIEW TOGGLE BUTTONS -->
        <div class="btn-group btn-group-sm me-3 shadow-sm">
          <button 
            class="btn btn-sm mb-0" 
            :class="viewMode === 'table' ? 'btn-primary text-white' : 'btn-outline-primary'" 
            @click="viewMode = 'table'"
          >
            <i class="fas fa-table"></i>
          </button>
          <button 
            class="btn btn-sm mb-0" 
            :class="viewMode === 'card' ? 'btn-primary text-white' : 'btn-outline-primary'" 
            @click="viewMode = 'card'"
          >
            <i class="fas fa-th-large"></i>
          </button>
        </div>

        <button type="button" class="btn btn-theme-primary mb-0" @click="showModal('add')">
          ADD DEVICE GROUP
        </button>
      </div>
    </div>
    <div class="card-body px-0 pt-0 pb-2">
      
      <!-- 1. TABLE VIEW -->
      <div v-if="viewMode === 'table'" class="table-responsive p-0">
        <table class="table align-items-center mb-0">
          <thead>
            <tr>
              <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 text-left">Device name</th>
              <!-- NEW COLUMN: Playlist Display -->
              <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2 text-left">Assigned Playlist</th>
              <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2 text-left">{{
                $t('status') }}</th>
              <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2 text-left">{{
                $t('action') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(role, index) in paginatedRoles" :key="index">

              <td class="text-left">
               <p class="text-xs font-weight-bold mb-0 text-dark">{{ role.device_group_name }}</p>
              </td>
              <!-- DISPLAY ASSIGNED PLAYLIST -->
              <td class="text-left">
                <p class="text-xs font-weight-bold mb-0" :class="role.playlist ? 'text-primary' : 'text-secondary'">
                  <i v-if="role.playlist" class="fas fa-play-circle me-1"></i>
                  {{ role.playlist ? role.playlist.playlist_name : 'No Playlist' }}
                </p>
              </td>
              <td class="text-left">
                <soft-badge :color="role.status == 1 ? 'success' : 'secondary'" variant="gradient" size="sm">{{
                  role.status == 1 ? 'Active' : "InActive" }}</soft-badge>
              </td>
              <td class="text-left">
                <a href="javascript:;" class="text-secondary font-weight-bold text-xs"
                  @click="showModal('edit', index)">
                  <i class="fas fa-edit"></i>
                </a>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- 2. CARD VIEW -->
      <div v-else class="px-3 px-md-4 pb-4 pt-3">
        <div class="row">
          <!-- UPDATED: Added col-12 for full width on mobile -->
          <div v-for="(role, index) in paginatedRoles" :key="'card-' + index" class="col-12 col-md-6 col-xl-3 mb-4">
            <div class="card h-100 border shadow-sm">
              <div class="card-body p-3">
                <div class="d-flex justify-content-between align-items-start mb-3">
                  <div class="d-flex align-items-center overflow-hidden">
                    <!-- Icon Background Synced with Theme -->
                    <div class="icon-shape icon-sm shadow text-center border-radius-md d-flex align-items-center justify-content-center me-2 theme-icon-bg flex-shrink-0" style="width: 32px; height: 32px;">
                      <i class="fas fa-layer-group text-white text-xs opacity-10"></i>
                    </div>
                   <h6 class="mb-0 text-sm text-dark text-truncate">{{ role.device_group_name }}</h6>
                  </div>
                  <a href="javascript:;" class="text-secondary" @click="showModal('edit', index)">
                    <i class="fas fa-edit text-xs"></i>
                  </a>
                </div>
                
                <!-- PLAYLIST ROW IN CARD -->
                <div class="mb-3">
                   <span class="text-xxs text-uppercase text-secondary font-weight-bolder d-block">Syncing Playlist</span>
                   <span class="text-xs font-weight-bold" :class="role.playlist ? 'text-primary' : 'text-dark'">
                    <i v-if="role.playlist" class="fas fa-play-circle me-1"></i>
                    {{ role.playlist ? role.playlist.playlist_name : 'None' }}
                   </span>
                </div>

                <div class="d-flex justify-content-between align-items-center pt-3 border-top">
                  <span class="text-xxs text-uppercase text-secondary font-weight-bolder">{{ $t('status') }}</span>
                  <soft-badge :color="role.status == 1 ? 'success' : 'secondary'" variant="gradient" size="sm">
                    {{ role.status == 1 ? 'Active' : "InActive" }}
                  </soft-badge>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- No Results Logic -->
      <div v-if="roles.length === 0" class="text-center py-4 text-secondary text-sm">
        No device groups found.
      </div>

      <!-- UPDATED: Added flex-wrap for mobile pagination screens -->
      <div class="pagination flex-wrap">
        <button @click="prevPage" :disabled="currentPage === 1">{{ $t('previous') }}</button>
        <button v-for="page in totalPages" :key="page" @click="goToPage(page)"
          :class="{ active: currentPage === page }">
          {{ page }}
        </button>
        <button @click="nextPage" :disabled="currentPage === totalPages">{{ $t('next') }}</button>
      </div>
    </div>

    <!-- Add Role Modal -->
    <div v-if="isAddModalVisible" class="modal fade show" style="display: block; background: rgba(0,0,0,0.5);" tabindex="-1" role="dialog"
      aria-labelledby="addRoleModalLabel" aria-hidden="true">
      <!-- UPDATED: Centered and responsive margin -->
      <div class="modal-dialog modal-dialog-centered px-3" role="document">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="addRoleModalLabel">Add Device Group</h5>
            <button type="button" class="close btn-modal-close" @click="hideModal('add')" aria-label="Close">
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
          <!-- UPDATED: max-height for mobile scrollability -->
          <div class="modal-body" style="max-height: 80vh; overflow-y: auto;">
            <form @submit.prevent="addRole">
              <div class="row">
                <div class="form-group col-md-12">
                  <label for="group_name">Group Name</label>
                  <input type="text" id="group_name" v-model="newRole.group_name" class="form-control"
                    placeholder="Enter group name" />
                  <span v-if="errors.group_name" class="text-danger">{{ errors.group_name }}</span>
                </div>
              </div>

              <div class="col-md-12">
                <div class="form-group">
                  <label for="device_id">Device List</label>
                  <multiselect v-model="newRole.device_id" :options="devices" :multiple="true" :close-on-select="false"
                    :clear-on-select="false" :preserve-search="true" placeholder="Pick some" label="device_name"
                    track-by="device_name"></multiselect>
                  <span v-if="errors.device_id" class="text-danger">{{ errors.device_id }}</span>
                </div>
              </div>

              <!-- NEW: Playlist Selection for Group -->
              <div class="col-md-12">
                <div class="form-group">
                  <label for="playlist_selection">Assign Playlist</label>
                  <select v-model="newRole.playlist_id" id="playlist_selection" class="form-control">
                    <option value="">Select Playlist (Optional)</option>
                    <option v-for="playlist in playlists" :key="playlist.id" :value="playlist.id">
                      {{ playlist.playlist_name }}
                    </option>
                  </select>
                  <small class="text-muted">This playlist will be pushed to all selected devices.</small>
                </div>
              </div>

              <div class="row mt-2">
                <div class="col-md-6"></div>
                <div class="col-md-6 col-12 d-flex justify-content-center justify-content-md-end">
                  <div class="form-group">
                    <label class="mr-2">Inactive</label>
                    <label class="switch">
                      <input type="checkbox" v-model="newRole.status" :true-value="'1'" :false-value="'0'">
                      <span class="slider round"></span>
                    </label>
                    <label class="ml-2">Active</label>
                  </div>
                </div>
              </div>

              <button type="submit" class="btn btn-theme-primary mt-3 w-100">
                <span v-if="isLoading" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                <span v-else>Add Group</span>
              </button>

            </form>
          </div>
        </div>
      </div>
    </div>

    <!-- Edit Role Modal -->
    <div v-if="isEditModalVisible" class="modal fade show" style="display: block; background: rgba(0,0,0,0.5);" tabindex="-1" role="dialog"
      aria-labelledby="editRoleModalLabel" aria-hidden="true">
      <div class="modal-dialog modal-dialog-centered px-3" role="document">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="editRoleModalLabel">Update Device Group</h5>
            <button type="button" class="close btn-modal-close" @click="hideModal('edit')" aria-label="Close">
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
          <div class="modal-body" style="max-height: 80vh; overflow-y: auto;">
            <form @submit.prevent="updateRole">

              <div class="row">
                <div class="form-group col-md-12">
                  <label for="name">Group Name</label>
                  <input type="text" id="name" v-model="editRole.device_group_name" class="form-control"
                    placeholder="Enter media name" />
                  <small v-if="errors.edit_device_group_name" class="text-danger">{{ errors.edit_device_group_name
                  }}</small>
                </div>
              </div>

              <div class="col-md-12">
                <div class="form-group">
                  <label for="device_id">Device List</label>
                  <multiselect v-model="editRole.device_id" :options="devices" :multiple="true" :close-on-select="false"
                    :clear-on-select="false" :preserve-search="true" placeholder="Pick some" label="device_name"
                    track-by="device_name"></multiselect>
                  <small v-if="errors.edit_device_id" class="text-danger">{{ errors.edit_device_id }}</small>
                </div>
              </div>

              <!-- NEW: Playlist Selection for Edit -->
              <div class="col-md-12">
                <div class="form-group">
                  <label for="edit_playlist_selection">Update Assigned Playlist</label>
                  <select v-model="editRole.playlist_id" id="edit_playlist_selection" class="form-control">
                    <option value="">Select Playlist (Optional)</option>
                    <option v-for="playlist in playlists" :key="playlist.id" :value="playlist.id">
                      {{ playlist.playlist_name }}
                    </option>
                  </select>
                  <small class="text-muted">Updating this will sync the new playlist to all group devices.</small>
                </div>
              </div>

              <div class="row">
                <div class="col-md-6 col-12 mx-auto">
                  <div class="form-group d-flex align-items-center justify-content-center">
                    <label class="mr-2">Inactive</label>
                    <label class="switch">
                      <input type="checkbox" v-model="editRole.status" :true-value="'1'" :false-value="'0'">
                      <span class="slider round"></span>
                    </label>
                    <label class="ml-2">Active</label>
                  </div>
                </div>
              </div>

              <button type="submit" class="btn btn-theme-primary w-100">
                <span v-if="isLoading" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                <span v-else>Update Group</span>
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>

  </div>
</template>
<script>
import resourcePhotoUrl from "@/assets/img/resource.png";
import SoftBadge from "@/components/SoftBadge.vue";
import '@fortawesome/fontawesome-free/css/all.css';
import DeviceGroupDataService from "../../services/DeviceGroupDataService.js";
import DeviceDataService from "../../services/DeviceDataService.js";
import PlayListDataService from "../../services/PlayListDataService.js"; // IMPORTED PLAYLIST SERVICE
import Swal from 'sweetalert2';
import Multiselect from 'vue-multiselect';

export default {
  name: "DeviceGroup",
  components: {
    SoftBadge,
    Multiselect
  },
  data() {
    return {
      viewMode: 'card', 
      isLoading: false,
      roles: [],
      devices: [],
      playlists: [], // NEW: Store active playlists
      newRole: {
        group_name: "",
        device_id: "",
        playlist_id: "", // NEW: Track playlist id
        status: '1'
      },
      editRole: {
        device_group_name: "",
        device_id: [],
        playlist_id: "", // NEW: Track playlist id
        status: "1"
      },
      resourcePhotoUrl,
      selectedIndex: null,
      isAddModalVisible: false,
      isEditModalVisible: false,
      selectedRoles: [], // For selecting roles in add mode
      currentPage: 1,
      itemsPerPage: 10,
      selectedLanguage: this.$i18n.locale,
      isImageSelected: true,
      isVideoSelected: false,
      errors: {}
    };
  },
  computed: {
    totalPages() {
      return Math.ceil(this.roles.length / this.itemsPerPage);
    },
    paginatedRoles() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.roles.slice(start, end);
    },
  },
  created() {
    this.getAllDeviceGroup();
    this.getAllDevices();
    this.getAllPlaylists(); // FETCH PLAYLISTS ON COMPONENT LOAD
  },
  methods: {
    switchLanguage(lang) {
      this.$i18n.locale = lang;
    },
    validateFields() {
      this.errors = {}; // Reset errors

      if (!this.newRole.group_name) {
        this.errors.group_name = "Group name is required.";
      }

      if (!this.newRole.device_id) {
        this.errors.device_id = "Please select a device name.";
      }

      return Object.keys(this.errors).length === 0; // Returns true if no errors
    },
    getAllDeviceGroup() {
      DeviceGroupDataService.getAll()
        .then(response => {
          this.roles = response.data;
          console.log(this.roles);
        })
        .catch(e => {
          console.log(e);
        });
    },
    getAllDevices() {
      DeviceDataService.getAllDevices()
        .then(response => {
          this.devices = response.data.map(device => ({
            id: device.id,
            device_name: device.device_name
          }));
          console.log(this.devices);
        })
        .catch(e => {
          console.log(e);
        });
    },
    // NEW METHOD: Get Active Playlists
    getAllPlaylists() {
      PlayListDataService.getAll()
        .then(response => {
          // Filter to show active playlists only
          this.playlists = response.data.filter(p => p.status == 1);
        })
        .catch(e => console.log(e));
    },
    addRole() {

      if (!this.validateFields()) {
        this.isLoading = false;
        return;
      }
      const payload = {
        device_group_name: this.newRole.group_name,
        device_id: this.newRole.device_id.map(device => device.id),
        playlist_id: this.newRole.playlist_id, // ADDED PLAYLIST ID TO PAYLOAD
        status: this.newRole.status
      };
      console.log(payload);

      try {
        DeviceGroupDataService.create(payload)
          .then(response => {
            console.log(response.data.devicegroup);

            if (response.data.devicegroup) {
              Swal.fire({
                icon: 'success',
                title: 'Success',
                text: 'Device group added successfully and synced to devices!',
                confirmButtonText: 'OK'
              }).then(() => {
                this.getAllDeviceGroup();
                this.hideModal('add');
              });
            }

          })
          .catch(e => {
            this.isLoading = false;
            console.log(e);
          });

      } catch (error) {
        console.error("Upload failed:", error);
        alert("Error uploading file.");
      }
    },
    showModal(mode, index = null) {
      if (mode === 'add') {
        this.isAddModalVisible = true;
        this.selectedRoles = [];
        this.newRole = { group_name: "", device_id: "", playlist_id: "", status: "1" };
      } else if (mode === 'edit' && index !== null) {
        this.selectedIndex = (this.currentPage - 1) * this.itemsPerPage + index;
        this.isEditModalVisible = true;

        // Clone the selected role's data into editRole to avoid direct mutation
        this.editRole = {
          device_group_name: this.roles[this.selectedIndex].device_group_name,
          device_id: this.roles[this.selectedIndex].devices.map(device => ({
            id: device.id,
            device_name: device.device_name
          })),
          playlist_id: this.roles[this.selectedIndex].playlist_id || "", // LOAD EXISTING PLAYLIST ID
          status: this.roles[this.selectedIndex].status
        };

      }
      this.isLoading = false;
    },
    onChangeErrorClear() {
      this.errors = {};
    },
    hideModal(mode) {
      if (mode === 'add') {
        this.isAddModalVisible = false;
      } else if (mode === 'edit') {
        this.isEditModalVisible = false;
      }
      this.selectedIndex = null;
      this.isLoading = false;
      this.errors = {};
    },
    updateRole() {
      this.errors = {};
      this.isLoading = true;

      if (!this.editRole.device_group_name) {
        this.errors.edit_device_group_name = "Group name is required.";
      }

      if (!this.editRole.device_id || this.editRole.device_id.length === 0) {
        this.errors.edit_device_id = "Please select at least one device.";
      }

      if (Object.keys(this.errors).length > 0) {
        this.isLoading = false;
        return;
      }

      const payload = {
        device_group_name: this.editRole.device_group_name,
        device_id: this.editRole.device_id.map(device => device.id),
        playlist_id: this.editRole.playlist_id, // ADDED PLAYLIST ID TO PAYLOAD
        status: this.editRole.status
      };

      DeviceGroupDataService.update(this.roles[this.selectedIndex].id, payload)
        .then(response => {
          if (response.data) {
            Swal.fire({
              icon: "success",
              title: "Success",
              text: "Device group updated and synced on all group devices!",
              confirmButtonText: "OK"
            }).then(() => {
              this.getAllDeviceGroup();
              this.hideModal('edit');
            });
          }
        })
        .catch(e => {
          this.isLoading = false;
          console.log(e);

          if (e.response && e.response.data && e.response.data.errors) {
            e.response.data.errors.forEach((error) => {
              if (error.code === "device_group_name") {
                this.errors.edit_device_group_name = error.message;
              }
            });
          }
        });
    },
    resetForm(mode) {
      if (mode === 'add') {
        this.newRole = { group_name: "", device_id: "", playlist_id: "", status: "1" };
        this.selectedRoles = [];
        this.isAddModalVisible = false;
      } else if (mode === 'edit') {
        this.selectedIndex = null;
        this.isEditModalVisible = false;
      }
    },
    prevPage() {
      if (this.currentPage > 1) {
        this.currentPage--;
      }
    },
    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++;
      }
    },
    goToPage(page) {
      if (page >= 1 && page <= this.totalPages) {
        this.currentPage = page;
      }
    },
  },
};
</script>

<style scoped>
/* ==========================
   DYNAMIC THEME SYNC
========================== */
.btn-theme-primary {
  background-color: var(--bs-primary) !important;
  color: white !important;
  border: 1px solid var(--bs-primary) !important;
  font-weight: 800 !important;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  transition: all 0.2s ease;
}
.btn-theme-primary:hover {
  filter: brightness(0.9);
  transform: translateY(-1px);
}

.btn-primary {
  background-color: var(--bs-primary) !important;
  border-color: var(--bs-primary) !important;
}

.btn-outline-primary {
  border: 1px solid var(--bs-primary) !important;
  color: var(--bs-primary) !important;
  background: transparent;
}
.btn-outline-primary:hover {
  background-color: var(--bs-primary) !important;
  color: white !important;
}

.theme-icon-bg {
  background-color: var(--bs-primary) !important;
}

.btn-modal-close {
  background-color: var(--bs-primary) !important;
  color: white !important;
  border: none;
  border-radius: 50%;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* ==========================
   PAGINATION SYNC
========================== */
.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 2rem;
  padding-bottom: 1rem;
}

.pagination button {
  background-color: var(--bs-primary) !important;
  color: white !important;
  border: none;
  padding: 8px 18px;
  border-radius: 8px;
  margin: 0.5rem; /* Updated for wrapping */
  font-size: 12px;
  font-weight: 800 !important;
  text-transform: uppercase;
  cursor: pointer;
  transition: 0.2s;
}

.pagination button:disabled {
  background-color: #e2e8f0 !important;
  color: #94a3b8 !important;
  cursor: not-allowed;
}

.pagination button.active {
  background-color: #ffffff !important;
  color: var(--bs-primary) !important;
  border: 2px solid var(--bs-primary) !important;
}

/* ==========================
   SWITCH SYNC
========================== */
.switch input:checked+.slider {
  background-color: var(--bs-primary) !important;
}

/* ==========================
   OTHER STYLES
========================== */
.langdrop {
  width: 130px;
  margin-left: 87%;
}

@media (min-width:300px) and (max-width:550px) {
  .langdrop {
    width: 130px;
    margin-left: 57%;
  }
}

.switch {
  position: relative;
  display: inline-block;
  width: 60px;
  height: 34px;
}

.switch input { opacity: 0; width: 0; height: 0; }

.slider {
  position: absolute;
  cursor: pointer;
  top: 0; left: 0; right: 0; bottom: 0;
  background-color: #ccc;
  transition: 0.4s;
  border-radius: 34px;
}

.slider:before {
  position: absolute;
  content: "";
  height: 26px; width: 26px;
  left: 4px; bottom: 4px;
  background-color: white;
  transition: 0.4s;
  border-radius: 50%;
}

input:checked+.slider:before {
  transform: translateX(26px);
}

.table th, .table td {
  text-align: left;
  padding: 12px;
}

.modal.fade.show {
  display: block;
  background-color: rgba(0, 0, 0, 0.5);
}

.modal-dialog { margin-top: 10%; }

.text-danger { font-size: 14px; }

/* --- ADDED MOBILE RESPONSIVE CSS --- */
@media (max-width: 768px) {
  .card-header {
    padding-bottom: 20px !important;
  }
  .modal-dialog {
    margin: 0.5rem;
  }
}
</style>